import {Component, inject, input, OnInit} from '@angular/core'
import {AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms'
import {MatCheckbox} from '@angular/material/checkbox'
import {MatError, MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'

import {MatInput} from '@angular/material/input'
import {MatOption, MatSelect} from '@angular/material/select'
import {FormatAccountDirective, FormatNumberDirective} from '@sparbanken-syd/sparbanken-syd-theme'
import {valueTitles} from '@sparbanken-syd/spb-savings-account-backend'
import {Interest} from '../../../services/interest.service'
import {OrientationService} from '../../../services/orientation.service'
import {RouteService} from '../../../services/route.service'
import {booleanOptions} from '../../../types'
import {FormUtils} from '../../../utils/form.utils'

@Component({
  selector: 'spb-bank-relationship',
  templateUrl: './bank-relationship.component.html',
  styleUrls: ['./bank-relationship.component.scss', '../kyc.component.scss'],
  imports: [ReactiveFormsModule, MatFormField, MatLabel, MatSelect, MatOption, MatInput, MatError, MatCheckbox, MatSuffix, FormatNumberDirective, MatIcon, FormatAccountDirective]
})
export class BankRelationshipComponent implements OnInit {
  public bankRelationshipForm = input.required<FormGroup>()
  public latestInterest = input.required<Interest>()

  public interestRunningTimeOptions: {
    value: string,
    label: string,
    interestRate: string
  }[] = []

  public interestDate = ''
  public savingsInterest = ''
  public minInterestRate = ''
  public maxInterestRate = ''
  public showDuplicateError = false
  public validateContact = false
  public customErrorStateMatcher = {
    isErrorState: (control: AbstractControl) => {
      // Only show error state when control is invalid AND touched
      return control && control.invalid && control.touched
    }
  }
  protected readonly routeService = inject(RouteService)
  protected readonly orientationService = inject(OrientationService)
  protected readonly yesOrNo = booleanOptions
  protected iHaveSpbAccount = 'Jag har ett konto på Sparbanken Syd som pengarna ska dras från'
  protected iWantNewAccount = `Jag vill öppna ett ${this.routeService.onHorseRoute() ? 'hästsparkonto' : 'sparkonto'}  som pengarna ska dras från`

  public toggleAccount(accountType: string) {
    const control = this.bankRelationshipForm().get(accountType)
    if (control) {
      control.setValue(!control.value)
    }
  }

  public onCheckboxChange(value: string | boolean, control: string, checked: boolean) {
    if (checked) {
      this.bankRelationshipForm().patchValue({[control]: value})
      this.validateContact = false
    } else {
      this.bankRelationshipForm().patchValue({[control]: null})
    }
  }

  public ngOnInit() {
    this.initializeInterestOptions()
    this.setupFormValidation()
    this.calculateInterestsToHTML()

    // If on horse route, set 'wantToBeContacted' to true automatically
    // since the checkbox option is hidden in the template.
    if (this.routeService.onHorseRoute()) {
      this.bankRelationshipForm().patchValue({wantToBeContacted: true})

      // Dynamic control for Horse/Farm confirmation
      if (!this.bankRelationshipForm().contains('hasHorseOrFarm')) {
        this.bankRelationshipForm().addControl('hasHorseOrFarm', new FormControl(false))
      }
      this.setupHorseValidation()
    }
  }


  private initializeInterestOptions(): void {
    // Reset options
    this.interestRunningTimeOptions = []
    const interest = this.latestInterest()


    // Format number to interest string
    const formatInterest = (rate: number): string => rate.toFixed(2).replace('.', ',')

    // Duration keys only (3, 6, 12, ...)
    const isDurationKey = (key: string): boolean => key === String(Number(key))

    for (const [key, value] of Object.entries(interest)) {
      /**
       * Hide the 3-month value since we added this after go live,
       * and it was not previously included.
       */
      if (key === '3') {
        continue
      }

      // Add auto interest option first
      if (key === '3-auto') {
        this.interestRunningTimeOptions.unshift({
          value: key,
          label: valueTitles[key],
          interestRate: formatInterest(value)
        })

        continue
      }

      // Add duration-based interest options
      if (isDurationKey(key)) {
        this.interestRunningTimeOptions.push({
          value: key,
          label: valueTitles[key],
          interestRate: formatInterest(value)
        })
      }
    }

    // Choose savings interest based on route
    const savingsRate = this.routeService.onHorseRoute()
      ? interest.horse
      : interest.eSpar

    this.savingsInterest = formatInterest(savingsRate)

    // Format date as YYYY-MM-DD
    this.interestDate = new Date(interest.interfaceDate).toLocaleDateString('sv-SE')
  }

  private setupFormValidation() {
    this.setupFixedRateAccountValidation()
    this.setupExistingSavingsAccountValidation()
    this.setupOpenSavingsAccountValidation()
  }

  private setupFixedRateAccountValidation() {
    this.bankRelationshipForm().controls.openFixedRateAccount.valueChanges.subscribe(value => {
      const validators = [Validators.required]
      const amountValidators = [...validators, Validators.min(50000), Validators.max(10 * 1000000)]

      FormUtils.addOrRemoveValidation(value, this.bankRelationshipForm().controls.interestRunningTime, validators)
      FormUtils.addOrRemoveValidation(value, this.bankRelationshipForm().controls.interestPlacementAmount, amountValidators)

      if (!value) {
        this.bankRelationshipForm().controls.existingSavingsAccount.setValue(false)
        this.bankRelationshipForm().controls.existingSavingsAccountNumber.setValue('')
      }

    })
  }

  private setupExistingSavingsAccountValidation() {
    this.bankRelationshipForm().controls.existingSavingsAccount.valueChanges.subscribe(value => {
      if (value) {
        this.handleDuplicateSelection(this.bankRelationshipForm().controls.openSavingsAccount)
        FormUtils.addOrRemoveValidation(true, this.bankRelationshipForm().controls.existingSavingsAccountNumber, [Validators.required, Validators.pattern(/^\d{10,}$/)])
        FormUtils.addOrRemoveValidation(false, this.bankRelationshipForm().controls.openSavingsAccount, [Validators.requiredTrue])
      } else {
        this.showDuplicateError = false
        FormUtils.addOrRemoveValidation(false, this.bankRelationshipForm().controls.existingSavingsAccountNumber, [Validators.required])
        FormUtils.addOrRemoveValidation(true, this.bankRelationshipForm().controls.openSavingsAccount, [Validators.requiredTrue])
      }
    })
  }

  private setupOpenSavingsAccountValidation() {
    this.bankRelationshipForm().controls.openSavingsAccount.valueChanges.subscribe(value => {
      if (value) {
        this.handleDuplicateSelection(this.bankRelationshipForm().controls.existingSavingsAccount)
      }
    })
  }

  // Handle validation for the horse confirmation checkbox
  private setupHorseValidation() {
    this.bankRelationshipForm().get('openSavingsAccount')?.valueChanges.subscribe(value => {
      const horseControl = this.bankRelationshipForm().get('hasHorseOrFarm')
      if (value) {
        // If user selects horse account, the checkbox is mandatory
        horseControl?.setValidators(Validators.requiredTrue)
      } else {
        // If deselected, reset validation and value
        horseControl?.clearValidators()
        horseControl?.setValue(false)
      }
      horseControl?.updateValueAndValidity()
    })
  }

  private handleDuplicateSelection(otherControl: AbstractControl) {
    if (otherControl.value) {
      this.bankRelationshipForm().controls['existingSavingsAccount'].setValue(false)
      this.bankRelationshipForm().controls['openSavingsAccount'].setValue(false)
      this.showDuplicateError = true
    } else {
      this.showDuplicateError = false
    }
  }

  private calculateInterestsToHTML() {
    const interestRates = this.interestRunningTimeOptions.map(option => parseFloat(option.interestRate.replace(',', '.')))

    this.minInterestRate = Math.min(...interestRates).toFixed(2).replace('.', ',')
    this.maxInterestRate = Math.max(...interestRates).toFixed(2).replace('.', ',')
  }

}